import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { List, PlugFill } from 'react-bootstrap-icons';
import { renderDashboardNavIcon } from '../DashboardSidebar/dashboardNavIcons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import { MenuCollapsibleSection } from '../shared/MenuCollapsibleSection';
import { isTrackTier } from '../../utils/authAccess';
import { DASHBOARD_ROUTES } from '../../constants/dashboardRoutes';
import {
  PRO_GOLD,
  NAV_ACCENT_GOLD,
  NAV_ACCENT_RED,
  getNavBadgeStyle,
  buildDashboardAccountSection,
  buildDashboardPrimarySections,
  buildMobileAccountExtras,
  DASHBOARD_LEARNING_SECTION,
  getTradeRoute,
} from '../DashboardSidebar/dashboardNavConfig';
import OutlinePrimaryButton from '../shared/OutlinePrimaryButton';
import ConnectBrokerModal from '../broker/ConnectBrokerModal';
import DisconnectBrokerModal from '../broker/DisconnectBrokerModal';
import PrimaryButton from '../shared/PrimaryButton';
import { isBrokerLinked, formatBrokerConnectionLabel, canAccessTradeWorkspace, canAccessMarketDataTools } from '../../utils/brokerConnection';

/**
 * Unified mobile menu — same nav sections as DashboardSidebar, right-aligned panel.
 * @param {boolean} alignBottom - Whether to align the trigger to the bottom of its flex row
 * @param {string} currentPage - Used for chart settings visibility and settings back navigation
 */
const MobileMenuDropdown = ({ alignBottom = false, currentPage = 'trade' }) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useContext(AuthContext);
  const { selectedAccount } = useContext(TradingContext);
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [panelTop, setPanelTop] = useState(60);
  const [showConnectBroker, setShowConnectBroker] = useState(false);
  const [showDisconnectBroker, setShowDisconnectBroker] = useState(false);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const suppressToggleRef = useRef(false);

  const scheduleToggleUnsuppress = useCallback(() => {
    window.setTimeout(() => {
      suppressToggleRef.current = false;
    }, 400);
  }, []);

  const closeMenu = useCallback(() => {
    suppressToggleRef.current = true;
    setShowMenuDropdown(false);
    scheduleToggleUnsuppress();
  }, [scheduleToggleUnsuppress]);

  const isSuperuser = user?.superuser === true;
  const hasPaidSubscription =
    user?.subscriptionStatus?.subscription &&
    ['active', 'trialing'].includes(user.subscriptionStatus.subscription.status);
  const showUpgradeToPro = isTrackTier(user);
  const tradeRoute = getTradeRoute(user);
  const showTrade = canAccessTradeWorkspace(user, selectedAccount) !== false;
  const brokerConnected = isBrokerLinked(user);
  const activeBrokerConn = user?.activeBrokerConnection ?? user?.brokerConnections?.[0];

  const openBrokerAction = () => {
    closeMenu();
    if (brokerConnected) setShowDisconnectBroker(true);
    else setShowConnectBroker(true);
  };

  const isOnTradeMobileShell =
    location.pathname === '/trade' || location.pathname.startsWith('/trade/');
  const showChartSettings = showTrade && isOnTradeMobileShell && currentPage === 'trade';

  const primarySections = buildDashboardPrimarySections(tradeRoute, {
    showTrade,
    showProBadges: showUpgradeToPro,
    showMarketDataPages: canAccessMarketDataTools(user, selectedAccount),
    showPositionsOverview: brokerConnected,
    showRsStudy: isSuperuser,
  });
  const accountSection = buildDashboardAccountSection({ showUpgradeToPro, isSuperuser });
  const mobileExtras = buildMobileAccountExtras({
    isSuperuser,
    hasPaidSubscription,
    isOnTradeShell: isOnTradeMobileShell,
  });

  const configurationSection = primarySections.find((s) => s.title === 'Configuration');
  const configurationWithChart = configurationSection
    ? {
        ...configurationSection,
        items: [
          ...(showChartSettings
            ? [{ type: 'chartSettings', label: 'Chart Settings' }]
            : []),
          ...configurationSection.items,
        ],
      }
    : null;
  const primarySectionsToRender = primarySections.map((section) =>
    section.title === 'Configuration' && configurationWithChart ? configurationWithChart : section
  );

  const toggleMenu = useCallback(() => {
    if (suppressToggleRef.current) return;
    setShowMenuDropdown((prev) => {
      if (prev) return false;
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setPanelTop(rect.bottom + 4);
      }
      return true;
    });
  }, []);

  useEffect(() => {
    if (!showMenuDropdown) return;
    const close = (e) => {
      const inButton = buttonRef.current && buttonRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inButton && !inPanel) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [showMenuDropdown, closeMenu]);

  const go = (fn) => {
    setShowMenuDropdown(false);
    fn();
  };

  const goToRoute = (path, navState) => {
    go(() => navigate(path, navState ? { state: navState } : undefined));
  };

  const menuItemStyle = {
    padding: '8px 12px 8px 24px',
    fontSize: '14px',
    fontWeight: 500,
    color: colors.text,
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 8,
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    textAlign: 'left',
  };

  const itemAccentStyle = (accent) => {
    if (accent === 'gold') return { color: NAV_ACCENT_GOLD };
    if (accent === 'red') return { color: NAV_ACCENT_RED };
    if (accent === 'pro') return { color: PRO_GOLD, fontWeight: 700 };
    return {};
  };

  const renderIcon = (icon) => renderDashboardNavIcon(icon, 14);

  const renderNavItem = (item) => {
    if (item.type === 'comingSoon') {
      return (
        <div
          key={item.label}
          style={{ ...menuItemStyle, cursor: 'default', opacity: 0.92 }}
          aria-disabled="true"
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{item.label}</span>
            <span style={getNavBadgeStyle('soon')}>{item.badge || 'Soon'}</span>
          </span>
        </div>
      );
    }

    if (item.type === 'upgrade') {
      return (
        <button
          key="upgrade"
          type="button"
          style={{ ...menuItemStyle, ...itemAccentStyle('pro') }}
          onClick={() => goToRoute('/pricing')}
        >
          <span>Upgrade to Trade</span>
        </button>
      );
    }

    if (item.type === 'chartSettings') {
      return (
        <button
          key="chart-settings"
          type="button"
          style={menuItemStyle}
          onClick={() => go(() => window.dispatchEvent(new Event('chartSettings:open')))}
        >
          <span>{item.label}</span>
        </button>
      );
    }

    if (item.type === 'reportBug') {
      return (
        <button
          key="report-bug"
          type="button"
          style={menuItemStyle}
          onClick={() => go(() => window.dispatchEvent(new Event('stateInspector:toggle')))}
        >
          {renderIcon('bug')}
          <span>{item.label}</span>
        </button>
      );
    }

    if (item.type === 'logout') {
      return (
        <button
          key="logout"
          type="button"
          style={menuItemStyle}
          onClick={() => go(() => logout())}
        >
          {renderIcon('logout')}
          <span>{item.label}</span>
        </button>
      );
    }

    if (item.type === 'link') {
      const icon =
        item.icon ||
        (item.to === DASHBOARD_ROUTES.settings
          ? 'gear'
          : item.to === '/contact'
            ? 'envelope'
            : item.to === '/admin'
              ? 'danger'
              : null);
      const openItem = () => {
        if (item.to === DASHBOARD_ROUTES.settings) {
          goToRoute(item.to, { from: location.pathname || currentPage });
        } else {
          goToRoute(item.to);
        }
      };
      return (
        <div key={item.to + item.label} style={{ display: 'flex', alignItems: 'center' }}>
          <button
            type="button"
            style={{
              ...menuItemStyle,
              ...itemAccentStyle(item.accent),
              flex: 1,
            }}
            onClick={openItem}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
              {icon ? renderIcon(icon) : null}
              <span>{item.label}</span>
              {item.badge ? (
                <span style={getNavBadgeStyle(item.badgeVariant || 'rose')}>{item.badge}</span>
              ) : null}
            </span>
          </button>
          {item.addTo ? (
            <button
              type="button"
              aria-label={item.addLabel || 'Add'}
              onClick={() => goToRoute(item.addTo)}
              style={{
                marginRight: 12,
                width: 22,
                height: 22,
                flexShrink: 0,
                border: 'none',
                borderRadius: 6,
                background: PRO_GOLD,
                color: '#111111',
                fontWeight: 700,
                fontSize: 16,
                lineHeight: 1,
                cursor: 'pointer',
              }}
            >
              +
            </button>
          ) : null}
        </div>
      );
    }

    return null;
  };

  const renderSection = (section) => (
    <MenuCollapsibleSection
      key={section.title + (section.isFirst ? '-first' : '')}
      title={section.title}
      defaultOpen={section.defaultOpen}
      isFirst={section.isFirst}
      colors={colors}
      titleStyle={{ color: colors.text, fontSize: 12 }}
      headerTrailing={
        section.headerAction?.type === 'link' ? (
          <OutlinePrimaryButton
            size="sm"
            onClick={() => goToRoute(section.headerAction.to)}
            style={{ fontSize: 11, padding: '4px 10px', minHeight: 26, borderRadius: 6 }}
          >
            {section.headerAction.label}
          </OutlinePrimaryButton>
        ) : null
      }
    >
      {section.items.map((item) => renderNavItem(item))}
    </MenuCollapsibleSection>
  );

  const panel = showMenuDropdown
    ? ReactDOM.createPortal(
        <>
          <div
            onMouseDown={(e) => {
              e.preventDefault();
              closeMenu();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              closeMenu();
            }}
            style={{ position: 'fixed', top: panelTop, left: 0, right: 0, bottom: 0, zIndex: 9998 }}
          />
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: panelTop,
              right: 0,
              width: '260px',
              borderRadius: '8px 0 0 8px',
              zIndex: 9999,
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              maxHeight: `calc(100dvh - ${panelTop + 8}px)`,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div
              style={{
                padding: '10px 12px',
                color: colors.textLight,
                borderBottom: `1px solid ${colors.borderLight}`,
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {user?.email || 'Account'}
            </div>

            {primarySectionsToRender.map((section) => renderSection(section))}
            {renderSection(DASHBOARD_LEARNING_SECTION)}
            {renderSection({
              ...accountSection,
              items: [...accountSection.items, ...mobileExtras],
            })}
            <div style={{ width: '100%', padding: '8px 12px 12px', boxSizing: 'border-box' }}>
              <PrimaryButton
                type="button"
                size="sm"
                onClick={openBrokerAction}
                style={{
                  width: '100%',
                  borderRadius: 8,
                  minWidth: 0,
                  background: brokerConnected
                    ? `linear-gradient(135deg, ${colors.danger || '#ef4444'}, #dc2626)`
                    : `linear-gradient(135deg, ${colors.purpleLight || colors.primaryLight}, ${colors.purple || colors.primary})`,
                  boxShadow: brokerConnected
                    ? `0 2px 8px ${(colors.danger || '#ef4444')}33`
                    : `0 2px 8px ${colors.purple || colors.primary}33`,
                }}
              >
                {brokerConnected ? (
                  'Disconnect Broker'
                ) : (
                  <>
                    <PlugFill size={14} />
                    Connect Broker
                  </>
                )}
              </PrimaryButton>
              {brokerConnected && activeBrokerConn && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 11,
                    color: colors.textMuted,
                    textAlign: 'center',
                  }}
                >
                  {formatBrokerConnectionLabel(activeBrokerConn)}
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )
    : null;

  return (
    <div ref={buttonRef} style={{ flexShrink: 0, alignSelf: alignBottom ? 'flex-end' : 'center' }}>
      <div
        role="button"
        tabIndex={0}
        aria-label={showMenuDropdown ? 'Close menu' : 'Open menu'}
        aria-expanded={showMenuDropdown}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleMenu();
          }
        }}
        onClick={toggleMenu}
        style={{
          padding: '6px 8px',
          backgroundColor: 'transparent',
          border: 'none',
          color: colors.text,
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'opacity 0.15s ease',
          outline: 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '0.7';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
      >
        <List size={20} />
      </div>
      {panel}
      <ConnectBrokerModal show={showConnectBroker} onHide={() => setShowConnectBroker(false)} />
      <DisconnectBrokerModal
        show={showDisconnectBroker}
        onHide={() => setShowDisconnectBroker(false)}
        connection={activeBrokerConn}
      />
    </div>
  );
};

export default MobileMenuDropdown;
